import React, { useState, useMemo, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import 'leaflet-extra-markers';

// Fullscreen control
import { FullscreenControl } from "react-leaflet-fullscreen";
import "react-leaflet-fullscreen/styles.css";

import { GoMoveToEnd } from "react-icons/go";
import { IoFilterSharp } from "react-icons/io5";

const statusColorsObject = {
    'fail': { color: 'red' },
    'active': { color: 'green' },
    'init': { color: 'purple' },
    'error': { color: 'orange' }
};

const getMarkerIcon = (status) => {
    let markerColor;

    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
        case 'fail':
            markerColor = 'red';
            break;
        case 'active':
            markerColor = 'green';
            break;
        case 'init':
            markerColor = 'purple';
            break;
        case 'error':
            markerColor = 'orange';
            break;
        default:
            markerColor = 'orange';
    }

    return L.ExtraMarkers.icon({
        markerColor: markerColor,
        shape: 'circle',
        prefix: 'fa',
    });
};

// Component to handle map centering
const RecenterMap = ({ center }) => {
    const map = useMap();

    useEffect(() => {
        if (center) {
            map.setView(center);
            map.setZoom(10);
        }
    }, [center, map]);

    return null;
};

const DevicesMap = forwardRef(({ center, zoom = 13, markers }, ref) => {
    const [selectedStatuses, setSelectedStatuses] = useState(['fail', 'active', 'init', 'error']);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [filterIsLoading, setFilterIsLoading] = useState(false);
    const mapRef = useRef(null);

    // Allow parent components to control map zoom and center
    useImperativeHandle(ref, () => ({
        zoomToCoordinates: (coords, zoomLevel) => {
            if (mapRef.current) {
                mapRef.current.setView(coords, zoomLevel);
            }
        },
    }));

    const handleMarkerClick = (marker) => {
        setSelectedMarker(marker);
    };

    const handleCheckboxChange = (status) => {
        setFilterIsLoading(true);

        setTimeout(() => {
            setSelectedStatuses((prev) =>
                prev.includes(status)
                    ? prev.filter((s) => s !== status)
                    : [...prev, status]
            );
        }, 300);
    };

    const memoizedMarkers = useMemo(() => {
        const filteredMarkers = markers
            .filter(
                (marker) =>
                    marker.latitude !== null &&
                    marker.longitude !== null &&
                    selectedStatuses.includes(marker.status)
            );

        // setFilterIsLoading(filteredMarkers.length === 0);
        setFilterIsLoading(false);

        return filteredMarkers.map((marker, index) => (
            <Marker
                key={index}
                position={[marker.latitude, marker.longitude]}
                icon={getMarkerIcon(marker.status)}
                eventHandlers={{
                    click: () => handleMarkerClick(marker),
                }}
            />
        ));
    }, [markers, selectedStatuses]);

    return (
        <div style={{ position: 'relative', zIndex: 0 }}>
            {/* Map */}
            <MapContainer
                center={center}
                zoom={zoom}
                ref={mapRef}
                attributionControl={false}
                style={{ height: '500px', width: '100%' }}
            >
                {/* Status Filter Checkboxes */}
                <div className='absolute top-[10px] left-[10px] bg-white p-2 rounded-md shadow-md z-[1000]'>
                    <div className='flex items-center justify-center'><IoFilterSharp /></div>
                    <p className='font-bold'>Filter by Status:</p>
                    <hr className='mt-2 mb-2' />
                    {['fail', 'active', 'init', 'error'].map((status) => (
                        <label key={status} className='mr-2 block text-start text-lg'>
                            <input
                                type="checkbox"
                                className='mr-2'
                                value={status}
                                checked={selectedStatuses.includes(status)}
                                onChange={() => handleCheckboxChange(status)}
                            />
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </label>
                    ))}
                </div>

                {filterIsLoading && (
                    <div className='absolute w-full h-full backdrop-blur-sm top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1000]'>
                        <div className='flex flex-col justify-center items-center gap-14 h-full'>
                            <div className='animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-black'></div>
                            <p className='font-bold text-black text-lg'>Filtering...</p>
                        </div>
                    </div>
                )}

                <RecenterMap center={center} />
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MarkerClusterGroup
                    maxClusterRadius={50}
                    chunkedLoading
                    spiderfyOnMaxZoom={false}
                    eventHandlers={{
                        clusterclick: (e) => {
                            const map = e.target._map; // Get the Leaflet map instance
                            const maxZoom = map.getMaxZoom(); // Get the maximum zoom level of the map
                            const currentZoom = map.getZoom(); // Get the current zoom level

                            if (currentZoom === maxZoom) {
                                const childCount = e.layer.getChildCount(); // Number of markers in the cluster
                                if (childCount > 100) {
                                    // Prevent spiderfying for clusters with more than 100 markers
                                    e.originalEvent.preventDefault();
                                } else {
                                    // Trigger spiderfying for clusters with 100 or fewer markers
                                    e.layer.spiderfy();
                                }
                            }
                        },
                    }}
                >
                    {memoizedMarkers}
                </MarkerClusterGroup>

                {selectedMarker && (
                    <Popup
                        position={[selectedMarker.latitude, selectedMarker.longitude]}
                        eventHandlers={{
                            remove: () => {
                                setSelectedMarker(null);
                            },
                        }}
                        offset={[0, -20]}
                    >
                        <div>
                            <div className='text-center'>
                                <h4 className='font-bold underline'>{selectedMarker.name || 'Unnamed Marker'}</h4>
                                <h4>{selectedMarker.deveui || 'No DevEUI'}</h4>
                            </div>
                            <div>
                                <div onClick={() => window.open(`/devices/${selectedMarker.deveui}/${selectedMarker.server}`, '_blank')}
                                    className="flex flex-row gap-2 items-center justify-center border mt-2 cursor-pointer hover:bg-gray-300 active:scale-95">
                                    Go to Device <GoMoveToEnd />
                                </div>
                            </div>
                            <div className='flex flex-row justify-center items-center gap-4'>
                                <p className='font-bold underline'>Status:</p>
                                <span
                                    className={`inline-flex items-center rounded-md bg-${statusColorsObject[selectedMarker.status].color} px-2 py-1 text-xs font-medium text-${statusColorsObject[selectedMarker.status].color} ring-1 ring-inset ring-green-600/20`}
                                >
                                    {selectedMarker.status}
                                </span>
                            </div>
                            <div className='flex flex-col text-center items-center justify-center'>
                                <p className='font-bold underline'>Signals:</p>
                                <table className="min-w-full text-sm border-collapse border border-gray-300">
                                    <tbody>
                                        <tr>
                                            <td className="px-2 py-2 border border-gray-300 font-bold">RSSI (avg)</td>
                                            <td className="px-2 py-2 border border-gray-300 font-bold">SNR (avg)</td>
                                        </tr>
                                        <tr>
                                            <td className="px-2 py-2 border border-gray-300">
                                                {selectedMarker.rssiavg !== null && selectedMarker.rssiavg !== undefined
                                                    ? selectedMarker.rssiavg
                                                    : 'RSSI not available'}
                                            </td>
                                            <td className="px-2 py-2 border border-gray-300">
                                                {selectedMarker.snravg !== null && selectedMarker.snravg !== undefined
                                                    ? selectedMarker.snravg
                                                    : 'SNR not available'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </Popup>
                )}

                <FullscreenControl position='topright' />
            </MapContainer>
        </div>
    );
});

export default DevicesMap;
